import {
  PropsWithChildren, RefObject, useEffect, useRef, useState,
} from 'react';
import {Portal} from '../Portal/Portal';
import {usePositionOverlay} from '../../utils';
import './Tooltip.scss';

export type TooltipProps<T extends HTMLElement> = {
  id: string;
  parentRef: RefObject<T>;
  className?: string;
  backgroundColor?: string;
  textColor?: string;
};

function thunkFromMany<T extends unknown[]>(...args: Array<(...a: T) => unknown>) {
  return (...called: T) => {
    for (const thunk of args) {
      thunk(...called);
    }
  };
}

function listen(...args: Parameters<Document['addEventListener']>) {
  document.addEventListener(...args);
  return () => {
    document.removeEventListener(...args);
  };
}

export function Tooltip<T extends HTMLElement>({id, parentRef, children, className, backgroundColor, textColor}: PropsWithChildren<TooltipProps<T>>) {
  const tooltipId = `tooltip-${id}`;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [overlayPos, recalculate] = usePositionOverlay(parentRef, tooltipRef, 'Right');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function handleActiveArea(fn: () => void) {
      return (evt: Event) => {
        if (parentRef.current === null) {
          return;
        }

        if (evt.target === parentRef.current || parentRef.current.contains(evt.target as Node)) {
          fn();
          recalculate();
        }
      };
    }

    const onActivate = handleActiveArea(() => setVisible(true));
    const onDeactivate = handleActiveArea(() => setVisible(false));

    return thunkFromMany(
      listen('mouseover', onActivate, true),
      listen('mouseout', onDeactivate, false),
    );
  }, [parentRef, recalculate]);

  const style: {
    top: string;
    left: string;
    backgroundColor?: string;
    color?: string;
  } = {
    top: `${overlayPos.top}px`,
    left: `${overlayPos.left}px`,
    backgroundColor: undefined,
    color: undefined,
  };

  if (backgroundColor) {
    style.backgroundColor = backgroundColor;
  }

  if (textColor) {
    style.color = textColor;
  }

  return visible
    ? (
      <Portal
        className='shared-tooltip-overlay'
        style={style}
      >
        <div role='tooltip' ref={tooltipRef} id={tooltipId} className={className}>{children}</div>
      </Portal>
    )
    : null;
}
